import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    TextField,
} from "@mui/material";
import RecipientChip from "./RecipientChip";
import { displayFullName } from "../../../../utilities/formatters";
import { preventDialogBackdropClick } from "../../../../utilities/generators";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";

import { getRecipients } from "../../../../reducers";
import msfActions from "../../../../actions/msfActions";

function RecipientsDialog({
    open,
    onCancel,
    onClose,
    onSave,
    onAddCustom,
    currentRecipients = [],
    max = null,
}) {
    const dispatch = useDispatch();
    const [selected, setSelected] = useState([]);
    const [searchText, setSearchText] = useState("");

    const recipients = useSelector(getRecipients) || [];
    const options = recipients
        .filter(
            (rec) =>
                [...currentRecipients, ...selected].findIndex(
                    (sel) => sel.uuid === rec.uuid
                ) < 0
        )
        .map((recipient) => displayFullName(recipient));

    return (
        <Dialog
            open={open}
            onClose={preventDialogBackdropClick(onClose)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>Add assessors</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={12}>
                            <Autocomplete
                                id="text-search-recipient"
                                freeSolo
                                value={searchText}
                                onChange={(event, value, reason) => {
                                    setSearchText("");
                                    if (
                                        !!value &&
                                        reason === "selectOption" &&
                                        selected.findIndex(
                                            (sel) =>
                                                displayFullName(sel) === value
                                        ) < 0
                                    ) {
                                        setSelected([
                                            ...selected,
                                            {
                                                ...recipients.find(
                                                    (rec) =>
                                                        displayFullName(rec) ===
                                                        value
                                                ),
                                                canDelete: true,
                                            },
                                        ]);
                                    }
                                }}
                                onInputChange={(event, value, reason) => {
                                    if (reason === "input") {
                                        setSearchText(value);
                                        if (value.length > 2) {
                                            dispatch(
                                                msfActions.getRecipients(
                                                    "P-MSF",
                                                    { value }
                                                )
                                            );
                                        }
                                    }
                                }}
                                options={options}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Name of assessor"
                                    />
                                )}
                                disabled={!!max && selected.length >= max}
                                fullWidth
                            />
                            <FormHelperText>
                                Search for an assessor
                            </FormHelperText>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            {selected.map((assessor) => (
                                <RecipientChip
                                    label={displayFullName(assessor)}
                                    status={assessor?.nomination?.status || ""}
                                    key={assessor.uuid}
                                    onDelete={
                                        !assessor.canDelete
                                            ? null
                                            : () =>
                                                  setSelected(
                                                      selected.filter(
                                                          ({ uuid }) =>
                                                              uuid !==
                                                              assessor.uuid
                                                      )
                                                  )
                                    }
                                />
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <Button
                                variant="contained"
                                color="neutral"
                                disableElevation
                                aria-label="add custom assessor"
                                onClick={onAddCustom}
                                startIcon={<PersonAddOutlinedIcon />}
                            >
                                Add
                            </Button>
                            <FormHelperText>
                                Manually add an assessor
                            </FormHelperText>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    variant="contained"
                    disableElevation
                    color="primary"
                    onClick={() => {
                        onSave(selected);
                        onClose();
                    }}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RecipientsDialog;
